import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { API } from "@/lib/shared";

export const FollowUpManagement = ({ patients }) => {
  const [followups, setFollowups] = useState([]);
  const [filter, setFilter] = useState('pending');

  const load = useCallback(async () => {
    try {
      const params = filter === 'all' ? {} : { status: filter };
      const res = await axios.get(`${API}/followups`, { params });
      setFollowups(res.data);
    } catch (e) { toast.error('Failed to load follow-ups'); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const markComplete = async (f) => {
    try {
      await axios.put(`${API}/followups/${f.id}`, { ...f, status: 'completed' });
      toast.success('Follow-up completed');
      load();
    } catch (e) { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this follow-up?')) return;
    try { await axios.delete(`${API}/followups/${id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error('Failed'); }
  };

  const patientName = (pid) => { const p = patients.find(x => x.id === pid); return p ? `${p.first_name} ${p.last_name}` : 'Unknown'; };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Follow-Up Calls</h2>
        <div className="flex gap-1 bg-muted/40 p-1 rounded-lg">
          {['pending', 'completed', 'all'].map(s => (
            <button key={s} onClick={() => setFilter(s)}
              className={`px-3 py-1 text-xs rounded-md transition-colors ${filter === s ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2 max-h-[500px] overflow-y-auto">
        {followups.map(f => (
          <div key={f.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
            <div className="flex-1">
              <p className="font-medium">{patientName(f.patient_id)}</p>
              <p className="text-xs text-muted-foreground">Due: {f.due_date} · {f.reason || 'No reason listed'}</p>
              {f.status === 'completed' && f.completed_at && <p className="text-xs text-emerald-400">✓ Completed {new Date(f.completed_at).toLocaleString()}</p>}
            </div>
            <div className="flex gap-1">
              {f.status === 'pending' && <Button size="sm" variant="outline" onClick={() => markComplete(f)} className="text-emerald-400 border-emerald-400/40 hover:bg-emerald-500/10">Complete</Button>}
              <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDelete(f.id)}><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
        ))}
        {followups.length === 0 && <p className="text-center text-muted-foreground py-8">No {filter !== 'all' ? filter : ''} follow-ups. Follow-ups are auto-created when you check "Schedule Follow-Up" on a visit.</p>}
      </div>
    </div>
  );
};

// ==================== EDUCATION LIBRARY ====================
